// ... import giữ nguyên như cũ
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleById, filterArticles } from '../../models/Article';
import Comment from '../../models/Comment';
import { getCurrentUser } from '../../models/Account';
import {
    Box,
    Typography,
    Divider,
    Grid,
    TextField,
    Button,
    Paper,
} from '@mui/material';
import ArticleCardList4 from '../Layout/ArticleCardList4';

const ArticleDetail = () => {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [comment, setComment] = useState('');
    const [commentList, setCommentList] = useState([]);
    const [visibleCount, setVisibleCount] = useState(5);
    const [loadingComments, setLoadingComments] = useState(false);
    const [user, setUser] = useState(null);
    const [replyText, setReplyText] = useState({});
    const [subVisibleCount, setSubVisibleCount] = useState({});

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                const response = await getArticleById(id);
                setArticle(response);

                if (response?.category_id) {
                    const res = await filterArticles({
                        category_id: response.category_id,
                        limit: 3,
                    });
                    const filtered = res.data.filter((a) => a.id !== response.id);
                    setRelatedArticles(filtered);
                }
            } catch (error) {
                throw error;
            }
        };

        fetchArticle();
    }, [id]);

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                getCurrentUser().then((u) => setUser(u));
            } else {
                setUser(null);
            }
        };
        fetchUser();
    }, []);

    const getAvatarUrl = (user) => {
        if (!user) return '';
        if (user.avatar) {
            return `${process.env.REACT_APP_STATIC_URL}${user.avatar}`;
        }
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || 'Ẩn danh')}&background=random`;
    };

    const loadComments = async () => {
        try {
            setLoadingComments(true);
            const res = await Comment.getCommentsByArticle(id);
            // Ban đầu không load subcomments
            const commentsWithEmptySubs = res.map(c => ({
                ...c,
                subcomments: [],
                subLoaded: false
            }));
            setCommentList(commentsWithEmptySubs);
        } catch (error) {
            throw error;
        } finally {
            setLoadingComments(false);
        }
    };

    const loadSubcomments = async (commentId) => {
        try {
            const subs = await Comment.getSubcomments(commentId);

            setCommentList(prev =>
                prev.map(c =>
                    c.id === commentId
                        ? { ...c, subcomments: subs, subLoaded: true }
                        : c
                )
            );
            setSubVisibleCount(prev => ({ ...prev, [commentId]: 3 }));
        } catch (error) {
            throw error;
        }
    };

    useEffect(() => {
        loadComments();
    }, [id]);

    const handleCommentSubmit = async () => {
        if (!comment.trim()) return;
        try {
            await Comment.createComment({
                target_type: 'article',
                target_id: id,
                account_id: user.id,
                content: comment,
            });
            setComment('');
            loadComments();
        } catch (error) {
            throw error;
        }
    };

    const handleReplySubmit = async (parentId) => {
        if (!replyText[parentId]?.trim()) return;
        try {
            await Comment.createComment({
                target_type: 'comment',
                target_id: parentId,
                parent_id: parentId,
                account_id: user.id,
                content: replyText[parentId],
            });
            setReplyText(prev => ({ ...prev, [parentId]: '' }));

            const newSub = {
                id: Date.now(),
                username: user.username,
                avatar: user.avatar,
                content: replyText[parentId],
                created_at: new Date().toISOString(),
            };

            setCommentList(prev =>
                prev.map(c =>
                    c.id === parentId
                        ? { ...c, subcomments: [...c.subcomments, newSub] }
                        : c
                )
            );
        } catch (error) {
            throw error;
        }
    };

    if (!article) return <Box p={4}>Đang tải bài viết...</Box>;

    const categoryMap = {
        luocsu: { label: 'Lược Sử', path: '/luocsu' },
        chienki: { label: 'Chiến Ký', path: '/chienky' },
    };
    const categoryInfo =
        categoryMap[article.category_name] || {
            label: article.category_name,
            path: '#',
        };

    return (
        <Box sx={{ maxWidth: '1300px', mx: 'auto', px: 2, py: 4 }}>
            <Grid container spacing={4}>
                <Grid item xs={false} md={2} />
                <Grid item xs={12} md={8}>
                    {/* Breadcrumb */}
                    <Typography variant="body2" sx={{ mb: 1, color: '#888' }}>
                        <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
                            Trang chủ
                        </Link>
                        {' / '}
                        <Link
                            to={categoryInfo.path}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                            {categoryInfo.label}
                        </Link>
                        {' / '}
                        {article.title}
                    </Typography>

                    {/* Tiêu đề */}
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {article.title}
                    </Typography>

                    {/* Ngày đăng */}
                    <Typography color="text.secondary" gutterBottom>
                        Ngày đăng bài:{" "}
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {new Intl.DateTimeFormat('vi-VN', {
                                timeZone: 'Asia/Ho_Chi_Minh',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            }).format(new Date(article.created_at))}
                        </Box>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Nội dung */}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            dangerouslySetInnerHTML={{ __html: article.content }}
                            sx={{
                                fontSize: '1.1rem',
                                lineHeight: 1.8,
                                mt: 2,
                                width: '100%',

                                // Đặt minWidth mặc định (mobile)
                                minWidth: '320px',

                                // Dùng media query cho màn hình lớn hơn md (~960px)
                                '@media (min-width:960px)': {
                                    minWidth: '1000px',
                                },

                                '& .video-wrapper': {
                                    position: 'relative',
                                    width: '100%',
                                    paddingBottom: '56.25%',
                                    height: 0,
                                    overflow: 'hidden',
                                    minWidth: '320px',
                                    '@media (min-width:960px)': {
                                        minWidth: '960px',
                                    },
                                },

                                '& .video-wrapper video': {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                },
                            }}
                        />
                    </Box>


                    {/* Bình luận */}
                    <Box sx={{ mt: 6 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Để lại ý kiến của bạn
                        </Typography>
                        {user ? (
                            <>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Nhập bình luận..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                />
                                <Button variant="contained" sx={{ mt: 2 }} onClick={handleCommentSubmit}>
                                    Gửi bình luận
                                </Button>
                            </>
                        ) : (
                            <Typography color="error" sx={{ mt: 1 }}>
                                Vui lòng đăng nhập để bình luận
                            </Typography>
                        )}

                        {/* Danh sách comment */}
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                {loadingComments ? 'Đang tải bình luận...' : 'Bình luận:'}
                            </Typography>

                            {commentList.slice(0, visibleCount).map((c) => (
                                <Paper key={c.id} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                                    {/* Header comment: avatar + tên + ngày */}
                                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                                        <Box
                                            component="img"
                                            src={getAvatarUrl(c)}
                                            alt={c.username || 'Ẩn danh'}
                                            sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                                        />
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                                                {c.username || 'Ẩn danh'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                                                {new Intl.DateTimeFormat('vi-VN', {
                                                    timeZone: 'Asia/Ho_Chi_Minh',
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                }).format(new Date(c.created_at))}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Nội dung comment */}
                                    <Typography variant="body1" sx={{ mt: 1, ml: 6 }}>
                                        {c.content}
                                    </Typography>

                                    {/* Xem phản hồi */}
                                    {!c.subLoaded && (
                                        <Button size="small" sx={{ mt: 1, ml: 6 }} onClick={() => loadSubcomments(c.id)}>
                                            Xem phản hồi
                                        </Button>
                                    )}

                                    {/* Subcomments */}
                                    {c.subLoaded && (
                                        <Box sx={{ ml: 6, mt: 2 }}>
                                            {c.subcomments.slice(0, subVisibleCount[c.id] || 3).map((sub) => (
                                                <Paper
                                                    key={sub.id}
                                                    elevation={0}
                                                    sx={{
                                                        p: 1,
                                                        mb: 1,
                                                        backgroundColor: '#f1f1f1',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                        <Box
                                                            component="img"
                                                            src={getAvatarUrl(sub)}
                                                            alt={sub.username || 'Ẩn danh'}
                                                            sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                                                        />
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                                                                {sub.username || 'Ẩn danh'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                                                                {new Date(sub.created_at).toLocaleString('vi-VN', {
                                                                    timeZone: 'Asia/Ho_Chi_Minh',
                                                                })}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ ml: 5 }}>
                                                        {sub.content}
                                                    </Typography>
                                                </Paper>
                                            ))}

                                            {subVisibleCount[c.id] < c.subcomments.length && (
                                                <Button
                                                    size="small"
                                                    onClick={() => setSubVisibleCount(prev => ({ ...prev, [c.id]: (prev[c.id] || 3) + 3 }))}
                                                >
                                                    Xem thêm phản hồi
                                                </Button>
                                            )}

                                            {/* Form nhập phản hồi */}
                                            {user ? (
                                                <Box sx={{ mt: 1, ml: 6 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Viết phản hồi..."
                                                        value={replyText[c.id] || ''}
                                                        onChange={(e) =>
                                                            setReplyText((prev) => ({ ...prev, [c.id]: e.target.value }))
                                                        }
                                                    />
                                                    <Button size="small" sx={{ mt: 1 }} onClick={() => handleReplySubmit(c.id)}>
                                                        Gửi phản hồi
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Typography color="error" sx={{ mt: 1 }}>
                                                    Đăng nhập để gửi phản hồi
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            ))}

                            {visibleCount < commentList.length && (
                                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setVisibleCount((prev) => prev + 5)}>
                                    Xem thêm bình luận
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Bài viết liên quan */}
                    <Box sx={{ mt: 6 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Bài viết liên quan
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <ArticleCardList4 articles={relatedArticles} />
                    </Box>
                </Grid>
                <Grid item xs={false} md={2} />
            </Grid>
        </Box >
    );
};

export default ArticleDetail;
